import { UseFormReturn, UseFormSetError } from 'react-hook-form'
import {
  QuestionsValidationErrors,
  validateAllQuestions,
} from '@core/components/templateAnswersForm'

// Maps validateAllQuestions output onto react-hook-form setError calls,
// prefixed with the form key ('questions' on most pages)
export const applyQuestionValidationErrors = (
  setError: UseFormSetError<any>,
  errors: QuestionsValidationErrors,
  questionsFormKey: string,
) => {
  Object.entries(errors.questions ?? {}).forEach(
    ([groupIndex, groupErrors]) => {
      if (groupErrors.conditionalQuestionGroup) {
        setError(
          `${questionsFormKey}.${groupIndex}.conditionalQuestionGroup.submissionText`,
          groupErrors.conditionalQuestionGroup,
        )
      }
      groupErrors.data?.forEach((questionError, questionIndex) => {
        if (questionError) {
          setError(
            `${questionsFormKey}.${groupIndex}.data.${questionIndex}`,
            questionError,
          )
        }
      })
    },
  )
}

export const getFirstErrorGroupIndex = (
  errors: QuestionsValidationErrors,
): number => {
  const groupIndexes = Object.keys(errors.questions ?? {})
    .map(Number)
    .filter(Number.isInteger)

  if (!groupIndexes.length) {
    return 0
  }

  return Math.min(...groupIndexes)
}

// Data-level questions validation for staged fill-out forms. Returns true when
// the stage may advance / the form may submit. Unlike react-hook-form's
// built-in validation this checks every question — mounted or not — which is
// what makes virtualized question lists safe to submit.
export const validateQuestionsStage = ({
  formContext,
  questionsFormKey,
  scrollToQuestionGroup,
}: {
  formContext: UseFormReturn<any>
  questionsFormKey: string
  scrollToQuestionGroup?: (groupIndex: number) => void
}): boolean => {
  const questions = formContext.getValues(questionsFormKey)
  const hiddenQuestionIds = formContext.getValues('hiddenQuestionIds')

  const errors = validateAllQuestions(questions, hiddenQuestionIds)
  if (!errors) {
    return true
  }

  applyQuestionValidationErrors(formContext.setError, errors, questionsFormKey)
  scrollToQuestionGroup?.(getFirstErrorGroupIndex(errors))
  return false
}
